import React from 'react';
import ModernCTA from '../../components/ModernCTA';

const WebDevelopment = () => {
  return (
    <div className="web-development-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>Modern Web Development <span className="highlight">Redefined</span></h1>
            <p className="subtitle">
              We engineer lightning-fast, SEO-optimized websites using React, Next.js, and Jamstack architecture to accelerate your digital growth.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">+300ms</span>
                <span className="stat-label">Average Page Load</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">95+</span>
                <span className="stat-label">Lighthouse Score</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">100%</span>
                <span className="stat-label">SEO Ready</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="technologies">
        <div className="container">
          <h2>Our <span className="highlight">Tech Stack</span></h2>
          <p className="section-description">
            We leverage cutting-edge technologies to deliver superior performance, security, and scalability.
          </p>
          
          <div className="tech-grid">
            <div className="tech-card">
              <div className="tech-icon react">⚛️</div>
              <h3>React.js</h3>
              <p>Component-based architecture for interactive UIs with optimal performance and maintainability.</p>
            </div>
            
            <div className="tech-card">
              <div className="tech-icon next">▲</div>
              <h3>Next.js</h3>
              <p>Hybrid static & server rendering, API routes, and built-in optimizations for production-grade apps.</p>
            </div>
            
            <div className="tech-card">
              <div className="tech-icon jamstack">🛠️</div>
              <h3>Jamstack</h3>
              <p>Modern architecture that delivers better performance, higher security, and lower scaling costs.</p>
            </div>
            
            <div className="tech-card">
              <div className="tech-icon graphql">🔷</div>
              <h3>GraphQL</h3>
              <p>Efficient data querying that gets exactly what you need in a single request.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <div className="benefits-text">
              <h2>Why Modern Web <span className="highlight">Development Matters</span></h2>
              <p>In today's competitive digital landscape, performance directly impacts your bottom line.</p>
              
              <div className="benefit-item">
                <div className="benefit-icon">⚡</div>
                <div>
                  <h3>Lightning Fast Performance</h3>
                  <p>Websites that load in under 1 second have 5x higher conversion rates. We optimize every asset and request.</p>
                </div>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">🔍</div>
                <div>
                  <h3>Built-in SEO Advantages</h3>
                  <p>Server-side rendering and static generation ensure your content gets indexed properly by search engines.</p>
                </div>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">🛡️</div>
                <div>
                  <h3>Enhanced Security</h3>
                  <p>Jamstack architecture reduces attack vectors by pre-rendering files and eliminating direct database access.</p>
                </div>
              </div>
            </div>
            
            <div className="benefits-visual">
              <div className="performance-metrics">
                <div className="metric-bar" style={{ height: '80%', backgroundColor: '#4f46e5' }}>
                  <span>Next.js</span>
                  <span>95/100</span>
                </div>
                <div className="metric-bar" style={{ height: '65%', backgroundColor: '#06b6d4' }}>
                  <span>Traditional SPA</span>
                  <span>72/100</span>
                </div>
                <div className="metric-bar" style={{ height: '40%', backgroundColor: '#64748b' }}>
                  <span>WordPress</span>
                  <span>55/100</span>
                </div>
              </div>
              <p className="metrics-caption">Lighthouse performance comparison (average scores)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process">
        <div className="container">
          <h2>Our <span className="highlight">Development Process</span></h2>
          <p className="section-description">
            A streamlined workflow that ensures quality at every stage
          </p>
          
          <div className="process-steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Discovery & Planning</h3>
              <p>Requirement analysis, architecture planning, and technology selection tailored to your goals.</p>
            </div>
            
            <div className="step">
              <div className="step-number">2</div>
              <h3>UI/UX Design</h3>
              <p>Creating intuitive user experiences with modern design principles and accessibility in mind.</p>
            </div>
            
            <div className="step">
              <div className="step-number">3</div>
              <h3>Development</h3>
              <p>Agile implementation with React components, API integrations, and performance optimizations.</p>
            </div>
            
            <div className="step">
              <div className="step-number">4</div>
              <h3>Testing & Optimization</h3>
              <p>Rigorous testing across devices, performance audits, and SEO verification.</p>
            </div>
            
            <div className="step">
              <div className="step-number">5</div>
              <h3>Deployment & Maintenance</h3>
              <p>CI/CD pipeline setup, hosting configuration, and ongoing support.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <ModernCTA />

      {/* Page Styles */}
      <style jsx>{`
        .web-development-page {
          margin-top: 100px;
          color: #f8fafc;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.6;
        }
        
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        /* Hero Styles */
        .hero {
          padding: 60px 0 80px;
          position: relative;
          overflow: hidden;
        }
        
        .hero-content {
          max-width: 800px;
          text-align: center;
          margin: 0 auto;
        }
        
        .hero h1 {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
          line-height: 1.2;
          background: linear-gradient(to right, #f8fafc 0%, #cbd5e1 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .highlight {
          background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .subtitle {
          font-size: 1.1rem;
          color: #cbd5e1;
          max-width: 700px;
          margin: 0 auto 2rem;
          padding: 0 15px;
        }
        
        .hero-stats {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 2rem;
          margin-top: 3rem;
        }
        
        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 100px;
        }
        
        .stat-number {
          font-size: 2rem;
          font-weight: 700;
          background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .stat-label {
          font-size: 0.85rem;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-top: 0.5rem;
          text-align: center;
        }
        
        /* Technologies Section */
        .technologies {
          padding: 70px 0;
          background: rgba(15, 23, 42, 0.7);
        }
        
        .technologies h2 {
          font-size: 2rem;
          text-align: center;
          margin-bottom: 1rem;
        }
        
        .section-description {
          text-align: center;
          max-width: 700px;
          margin: 0 auto 2rem;
          color: #94a3b8;
          font-size: 1rem;
          padding: 0 15px;
        }
        
        .tech-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
        }
        
        .tech-card {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 12px;
          padding: 1.5rem;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .tech-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(15, 23, 42, 0.5);
          background: rgba(30, 41, 59, 0.8);
        }
        
        .tech-icon {
          font-size: 2rem;
          margin-bottom: 1rem;
        }
        
        .tech-card h3 {
          font-size: 1.3rem;
          margin-bottom: 0.8rem;
          color: #f8fafc;
        }
        
        .tech-card p {
          color: #94a3b8;
          font-size: 0.95rem;
        }
        
        /* Benefits Section */
        .benefits {
          padding: 70px 0;
          background: rgba(15, 23, 42, 0.5);
        }
        
        .benefits h2 {
          font-size: 2rem;
          margin-bottom: 1.2rem;
        }
        
        .benefits-text {
          max-width: 100%;
        }
        
        .benefits-text p {
          color: #94a3b8;
          margin-bottom: 1.5rem;
          font-size: 1rem;
        }
        
        .benefit-item {
          display: flex;
          gap: 1.2rem;
          margin-bottom: 1.8rem;
        }
        
        .benefit-icon {
          font-size: 1.3rem;
          flex-shrink: 0;
          margin-top: 0.2rem;
        }
        
        .benefit-item h3 {
          font-size: 1.1rem;
          margin-bottom: 0.5rem;
          color: #f8fafc;
        }
        
        .benefit-item p {
          font-size: 0.95rem;
          margin: 0;
        }
        
        .benefits-visual {
          margin-top: 3rem;
        }
        
        .performance-metrics {
          display: flex;
          height: 250px;
          align-items: flex-end;
          gap: 1rem;
          padding: 0 0.5rem;
        }
        
        .metric-bar {
          flex: 1;
          border-radius: 8px 8px 0 0;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 0.8rem;
          color: white;
          font-weight: 600;
          transition: height 0.5s ease;
          font-size: 0.9rem;
        }
        
        .metrics-caption {
          text-align: center;
          margin-top: 1rem;
          color: #94a3b8;
          font-size: 0.85rem;
        }
        
        /* Process Section */
        .process {
          padding: 70px 0;
        }
        
        .process h2 {
          font-size: 2rem;
          text-align: center;
          margin-bottom: 1rem;
        }
        
        .process-steps {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          margin-top: 3rem;
        }
        
        .step {
          position: relative;
          padding: 1.8rem 1.2rem;
          background: rgba(30, 41, 59, 0.5);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: transform 0.3s ease;
        }
        
        .step:hover {
          transform: translateY(-5px);
          background: rgba(30, 41, 59, 0.8);
        }
        
        .step-number {
          position: absolute;
          top: -20px;
          left: 15px;
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          font-weight: 700;
          color: white;
        }
        
        .step h3 {
          font-size: 1.2rem;
          margin: 1.2rem 0 0.8rem;
          color: #f8fafc;
        }
        
        .step p {
          color: #94a3b8;
          font-size: 0.9rem;
        }
        
        /* Responsive Adjustments */
        @media (min-width: 640px) {
          .hero {
            padding: 80px 0 100px;
          }
          
          .hero h1 {
            font-size: 3rem;
          }
          
          .subtitle {
            font-size: 1.15rem;
          }
          
          .hero-stats {
            gap: 2.5rem;
          }
          
          .stat-number {
            font-size: 2.2rem;
          }
          
          .process-steps {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          }
        }
        
        @media (min-width: 768px) {
          .web-development-page {
            margin-top: 115px;
          }
          
          .hero {
            padding: 100px 0 120px;
          }
          
          .hero h1 {
            font-size: 3.5rem;
          }
          
          .subtitle {
            font-size: 1.25rem;
            margin-bottom: 3rem;
          }
          
          .hero-stats {
            gap: 3rem;
            margin-top: 4rem;
          }
          
          .stat-number {
            font-size: 2.5rem;
          }
          
          .technologies {
            padding: 100px 0;
          }
          
          .technologies h2 {
            font-size: 2.5rem;
          }
          
          .section-description {
            font-size: 1.1rem;
            margin-bottom: 3rem;
          }
          
          .tech-grid {
            gap: 2rem;
            margin-top: 3rem;
          }
          
          .tech-card {
            padding: 2rem;
          }
          
          .tech-icon {
            font-size: 2.5rem;
            margin-bottom: 1.5rem;
          }
          
          .tech-card h3 {
            font-size: 1.5rem;
          }
          
          .benefits {
            padding: 100px 0;
          }
          
          .benefits h2 {
            font-size: 2.5rem;
          }
          
          .benefits-content {
            display: flex;
            gap: 4rem;
            align-items: center;
          }
          
          .benefits-text {
            max-width: 600px;
          }
          
          .benefits-visual {
            margin-top: 0;
            flex: 1;
          }
          
          .performance-metrics {
            height: 300px;
            gap: 1.5rem;
            padding: 0 1rem;
          }
          
          .metric-bar {
            padding: 1rem;
            font-size: 1rem;
          }
          
          .process {
            padding: 100px 0;
          }
          
          .process h2 {
            font-size: 2.5rem;
          }
          
          .process-steps {
            gap: 2rem;
            margin-top: 4rem;
          }
          
          .step {
            padding: 2rem 1.5rem;
          }
          
          .step-number {
            top: -25px;
            left: 20px;
            width: 50px;
            height: 50px;
            font-size: 1.5rem;
          }
          
          .step h3 {
            font-size: 1.25rem;
            margin: 1.5rem 0 1rem;
          }
        }
        
        @media (min-width: 1024px) {
          .process-steps {
            grid-template-columns: repeat(5, 1fr);
          }
        }
      `}</style>
    </div>
  );
};

export default WebDevelopment;